import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { toggleSource, resetToDefaults, toggleMetadataFilter } from '../redux/slices/settingsSlice';

const Container = styled.div`
  padding: 40px 60px;
  color: white;
  min-height: 100vh;
`;

const Title = styled.h1`
  font-size: 3rem;
  margin-bottom: 30px;
`;

const Section = styled.div`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 20px;
`;

const SourceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const SourceItem = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 15px 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  margin-right: 15px;
  cursor: pointer;
`;

const SourceName = styled.span`
  font-size: 1.2rem;
`;

const SourceBadge = styled.span`
  margin-left: auto;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 0.85rem;
  background: ${props => {
    switch(props.$source) {
      case 'WatchSeries': return 'rgba(229, 9, 20, 0.9)';
      case 'GoodMoviesHD': return 'rgba(46, 211, 105, 0.9)';
      case '0123Movie': return 'rgba(59, 130, 246, 0.9)';
      case 'MegaShareMovie': return 'rgba(168, 85, 247, 0.9)';
      case 'PlutoTV': return 'rgba(251, 146, 60, 0.9)';
      case 'TubiTV': return 'rgba(236, 72, 153, 0.9)';
      case 'Plex': return 'rgba(234, 179, 8, 0.9)';
      case 'RokuChannel': return 'rgba(20, 184, 166, 0.9)';
      case 'XumoPlay': return 'rgba(99, 102, 241, 0.9)';
      default: return 'rgba(239, 68, 68, 0.9)';
    }
  }};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 30px;
`;

const ResetButton = styled.button`
  padding: 12px 24px;
  background: rgba(229, 9, 20, 0.9);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(229, 9, 20, 1);
  }
`;

const InfoText = styled.p`
  color: rgba(255, 255, 255, 0.7);
  margin-top: 15px;
  font-size: 0.95rem;
`;

const Settings = () => {
  const dispatch = useDispatch();
  const { enabledSources, allSources, hideIncompleteMetadata } = useSelector(state => state.settings);

  const handleToggle = (source) => {
    dispatch(toggleSource(source));
  };

  const handleMetadataToggle = () => {
    dispatch(toggleMetadataFilter());
  };

  const handleReset = () => {
    if (window.confirm('Reset to default sources? (Plex, Tubi, Roku, Xumo, PlutoTV)')) {
      dispatch(resetToDefaults());
    }
  };

  return (
    <Container>
      <Title>Settings</Title>

      <Section>
        <SectionTitle>Source Filters</SectionTitle>
        <InfoText>
          Select which streaming sources to include when browsing movies and TV shows.
          Changes are saved automatically.
        </InfoText>

        <SourceList style={{ marginTop: '20px' }}>
          {allSources.map(source => (
            <SourceItem key={source}>
              <Checkbox
                type="checkbox"
                checked={enabledSources.includes(source)}
                onChange={() => handleToggle(source)}
              />
              <SourceName>{source}</SourceName>
              <SourceBadge $source={source}>
                {enabledSources.includes(source) ? 'Enabled' : 'Disabled'}
              </SourceBadge>
            </SourceItem>
          ))}
        </SourceList>

        <ButtonGroup>
          <ResetButton onClick={handleReset}>
            Reset to Defaults
          </ResetButton>
        </ButtonGroup>
      </Section>

      <Section>
        <SectionTitle>Content Filters</SectionTitle>
        <InfoText>
          Control which movies and TV shows are displayed based on content quality.
          Changes are saved automatically and take effect immediately.
        </InfoText>

        <SourceList style={{ marginTop: '20px' }}>
          <SourceItem>
            <Checkbox
              type="checkbox"
              checked={hideIncompleteMetadata}
              onChange={handleMetadataToggle}
            />
            <SourceName>Hide movies with incomplete metadata</SourceName>
            <SourceBadge $source={hideIncompleteMetadata ? 'enabled' : 'disabled'} style={{
              background: hideIncompleteMetadata ? 'rgba(46, 211, 105, 0.9)' : 'rgba(109, 109, 110, 0.7)'
            }}>
              {hideIncompleteMetadata ? 'Enabled' : 'Disabled'}
            </SourceBadge>
          </SourceItem>
        </SourceList>

        <InfoText style={{ marginTop: '15px', fontSize: '0.85rem' }}>
          This will hide movies that are missing key information such as ratings, genres, 
          poster images, or runtime details. Helps ensure only well-documented content is displayed.
        </InfoText>
      </Section>
    </Container>
  );
};

export default Settings;
